import React, { useEffect, useState } from 'react';
import { Formik, FormikProps } from 'formik';
import { PaymentUtils, TranslationUtils, ValidationUtils } from '../../utils';
import './PaymentForm.scss';
import { Button } from '..';
import { Guests } from 'guest-data-types-ts-lib';
import { getCardType } from '../../utils/creditCardValidation.utils';
import { AddressFields } from './AddressFields';
import * as Yup from 'yup';
import { ValidationError } from 'yup';
import * as _ from 'lodash';
import { LocalityConfig } from './addressFields.conf';
import { ImageAmexCard, ImageDiscoverCard, ImageMasterCard, ImageVisaCard } from '../../assets/images';
import { Currency } from '../../types/preference.type';

export interface PaymentFormValues {
    first_name: string;
    last_name: string;
    country: string;
    billing_address: string;
    city: string;
    state: string;
    zip_code: string;
    card_number: string;
    expiration_month: string;
    expiration_year: string;
    security_code: string;
}

interface PaymentFormProps {
    guests: Guests;
    currency: Currency;
    onSubmit: (values: PaymentFormValues) => Promise<any>;
}

export const PaymentForm: React.FC<PaymentFormProps> = (props) => {
    const { guests, onSubmit, currency } = props;
    // ALLOW DISCOVER PAYMENTS
    const isDiscoverEnabled = true;

    const initialFormSchema = {
        first_name: Yup.string().required().max(50),
        last_name: Yup.string().required().max(50),
        country: Yup.string().required().length(2),
        billing_address: Yup.string().required().max(100),
        city: Yup.string().required().max(100),
        state: Yup.string().required().max(100),
        zip_code: Yup.string().required().max(100),
        card_number: Yup.string().required(),
        expiration_month: Yup.string().required().length(2),
        expiration_year: Yup.string().required().length(4),
        security_code: Yup.string()
            .matches(new RegExp(/^[0-9]{3,4}$/))
            .required(),
    };

    const [isLoading, setIsLoading] = useState(false);
    const [cardNumber, setCardNumber] = useState('');
    const [formSchema, setFormSchema] = useState(Yup.object().shape(initialFormSchema));

    const initialValues: PaymentFormValues = {
        first_name: guests.primary.first_name || '',
        last_name: guests.primary.last_name || '',
        country: 'US',
        billing_address: '',
        city: '',
        state: '',
        zip_code: '',
        card_number: '',
        expiration_month: '0',
        expiration_year: '0',
        security_code: '',
    };

    const activeCard: { [key: string]: string } = {
        Visa: '',
        MasterCard: '',
        Discover: '',
        AmEx: '',
    };

    useEffect(() => {
        const { type: cardType } = getCardType(cardNumber);
        for (const type in activeCard) {
            if (cardType === '' || cardType === type) {
                activeCard[type] = 'active';
                return;
            }
            activeCard[type] = '';
        }
    }, [cardNumber]);

    const validateForm = async (values: PaymentFormValues): Promise<any> => {
        let validationErrors: { [key: string]: string } = {};
        const { type: cardType } = getCardType(cardNumber);
        try {
            await formSchema.validate(values, { abortEarly: false });
        } catch (error) {
            (error.inner || []).forEach((e: ValidationError) => {
                if (e.path) {
                    validationErrors[e.path] = e.message;
                }
            }, validationErrors);
        } finally {
            const shouldExcludeDiscover = !isDiscoverEnabled && cardType == 'Discover';
            const shouldExcludeAmex = currency != Currency.USD && cardType == 'AmEx';
            if (!ValidationUtils.isValidCreditCard(values.card_number) || shouldExcludeDiscover || shouldExcludeAmex) {
                validationErrors['card_number'] = 'Invalid card number';
            }

            if (!ValidationUtils.isValidExpirationYear(values.expiration_year)) {
                validationErrors['expiration_year'] = 'Invalid expiration year';
            }

            if (!ValidationUtils.isValidExpirationMonth(values.expiration_month, values.expiration_year)) {
                validationErrors['expiration_month'] = 'Invalid expiration month';
            }
        }

        if (Object.keys(validationErrors).length) {
            return validationErrors;
        }
    };

    const handleAddressFieldValidationChange = (localityConfig: LocalityConfig[], formikProps: FormikProps<PaymentFormValues>) => {
        const newSchema: { [key: string]: any } = {};

        const fieldsForCountry = new Set<string>(_.map(localityConfig, (conf) => conf.storeKey));

        for (const field of ['city', 'state', 'zip_code']) {
            formikProps.setFieldValue(field, '');
            if (!fieldsForCountry.has(field)) {
                formikProps.setFieldError(field, undefined);
                newSchema[field] = Yup.string().optional();
            }
        }

        for (const conf of localityConfig) {
            if (!('options' in conf.fieldConfig) && conf.fieldConfig.format) {
                newSchema[conf.storeKey] = Yup.string().matches(new RegExp(conf.fieldConfig.format));
            } else {
                newSchema[conf.storeKey] = initialFormSchema[conf.storeKey];
            }
        }

        setFormSchema(Yup.object().shape(_.merge(initialFormSchema, newSchema)));
    };

    const handleSubmit = async (values: PaymentFormValues) => {
        setIsLoading(true);
        await onSubmit(values);
        setIsLoading(false);
    };

    return (
        <div id={'payment-form'}>
            <div className={'user-input-panel d-print-none w-100 px-16 py-16'}>
                <div className="interline-payment-form" />
                <div>
                    <span tabIndex={0} className="heading-small title-reservation-medium">
                        {TranslationUtils.get('payment_form_billing_information')}
                    </span>
                </div>
                <Formik initialValues={initialValues} validate={validateForm} onSubmit={handleSubmit} validateOnMount={true}>
                    {(props) => {
                        const { values, errors, touched, handleChange, handleBlur, handleSubmit, isValid } = props;
                        return (
                            <form onSubmit={handleSubmit}>
                                <div className="form-row mt-16">
                                    <div className="col-md-6 col-lg-6">
                                        <label className="body-regular col-form-label  type-body" htmlFor="first-name">
                                            {TranslationUtils.get('payment_form_billing_name')}
                                        </label>
                                        <input
                                            id="first-name"
                                            aria-label="Billing first name"
                                            aria-required="true"
                                            className={`body-regular w-100 form-control ${
                                                errors.first_name && touched.first_name ? 'invalid' : ''
                                            }`}
                                            type="text"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.first_name}
                                            name="first_name"
                                        />
                                    </div>
                                    <div className="col-md-6 col-lg-6 pt-8 pt-md-0">
                                        <div className="interline-payment-form" />
                                        <input
                                            id="last-name"
                                            aria-label="Billing last name"
                                            aria-required="true"
                                            className={`body-regular w-100 form-control ${
                                                errors.last_name && touched.last_name ? 'invalid' : ''
                                            }`}
                                            type="text"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.last_name}
                                            name="last_name"
                                        />
                                    </div>
                                </div>

                                <AddressFields
                                    initialCountry={initialValues.country}
                                    formikProps={props}
                                    onFieldsChange={handleAddressFieldValidationChange}
                                />

                                <div className="form-row mt-16">
                                    <div className="col-12">
                                        <label className="body-regular col-form-label type-body" htmlFor="">
                                            {TranslationUtils.get('payment_form_card_number')}
                                        </label>
                                        {setCardNumber(values.card_number)}
                                        <input
                                            id="card_number"
                                            aria-label="card_number"
                                            aria-required="true"
                                            className={`body-regular w-100 form-control ${
                                                errors.card_number && touched.card_number ? 'invalid' : ''
                                            }`}
                                            type="text"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={cardNumber}
                                            name="card_number"
                                            maxLength={16}
                                        />
                                    </div>
                                </div>
                                <div className="form-row mt-16">
                                    <div className="col-12 col-md-6 col-lg-6">
                                        <label className="body-regular col-form-label type-body" htmlFor="">
                                            {TranslationUtils.get('payment_form_expiration_date')}
                                        </label>
                                        <select
                                            aria-required="true"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.expiration_month}
                                            name="expiration_month"
                                            className={`body-regular w-100 custom-select ${
                                                errors.expiration_month && touched.expiration_month ? 'invalid' : ''
                                            }`}
                                        >
                                            <option className={'body-regular'} value="0" disabled={true}>
                                                {TranslationUtils.get('payment_form_month')}
                                            </option>
                                            {PaymentUtils.getCreditCardExpirationMonths().map((item, key) => {
                                                return (
                                                    <option className={'body-regular'} key={key} value={item.value}>
                                                        {item.value}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                    <div className="col-12 col-md-6 col-lg-6 pt-8 pt-md-0">
                                        <div className="interline-payment-form" />
                                        <select
                                            aria-required="true"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.expiration_year}
                                            name="expiration_year"
                                            className={`body-regular w-100 custom-select ${
                                                errors.expiration_year && touched.expiration_year ? 'invalid' : ''
                                            }`}
                                        >
                                            <option className={'body-regular'} value="0" disabled={true}>
                                                {TranslationUtils.get('payment_form_year')}
                                            </option>
                                            {PaymentUtils.getCreditCardExpirationYears().map((item, key) => {
                                                return (
                                                    <option className={'body-regular'} key={key} value={item.value}>
                                                        {item.value}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                </div>
                                <div className="form-row mt-16">
                                    <div className="col-12 col-md-6 col-lg-6">
                                        <label className="body-regular col-form-label type-body" htmlFor="">
                                            {TranslationUtils.get('payment_form_security_code')}
                                        </label>
                                        <input
                                            id="security_code"
                                            aria-label="Security Code"
                                            aria-required="true"
                                            className={`body-regular w-100 form-control ${
                                                errors.security_code && touched.security_code ? 'invalid' : ''
                                            }`}
                                            type="text"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.security_code}
                                            name="security_code"
                                            maxLength={4}
                                        />
                                    </div>
                                </div>
                                <div className="form-row mt-16 text-md-left">
                                    <p tabIndex={0} className="body-regular col-12">
                                        {TranslationUtils.get('payment_form_payments_note')}
                                    </p>
                                </div>
                                <div className="form-row text-md-left">
                                    <div className="m-auto m-md-0">
                                        <ImageVisaCard className={`icon-svg icon-card ${activeCard.Visa}`} id="icon-visa" />
                                        <ImageMasterCard className={`icon-svg icon-card ${activeCard.MasterCard}`} id="icon-mastercard" />
                                        {isDiscoverEnabled && (
                                            <ImageDiscoverCard className={`icon-svg icon-card ${activeCard.Discover}`} id="icon-discover" />
                                        )}
                                        {currency === Currency.USD && (
                                            <ImageAmexCard className={`icon-svg icon-card ${activeCard.AmEx}`} id="icon-amex" />
                                        )}
                                    </div>
                                </div>

                                <div className="form-row mt-16">
                                    <div className="col-12 col-md-6 d-none d-lg-block" />
                                    <div className="interline-payment-form" />
                                    <div className={'col-md-6 col-sm-12 float-left payment-adjustment-button'}>
                                        <Button
                                            id={'body-regular button-payment-form-submit'}
                                            type="submit"
                                            onClick={() => {}}
                                            isLoading={isLoading}
                                            label={TranslationUtils.get('payment_form_pay_now')}
                                            variant={'primary'}
                                            disabled={isLoading || !isValid}
                                        />
                                    </div>
                                </div>
                            </form>
                        );
                    }}
                </Formik>
            </div>
        </div>
    );
};
